import * as React from 'react'
import { O, pipe, RA, RD } from '@packages/prelude'
import * as domain from '@packages/domain'
import { useAvailableNames } from './useAvailableNames'
import { UserUploadControl } from './UserUploadControl'
import { AudioRecorderWrapper, TextContainer } from './styles'
import { discard, objectUrlOf, setGreetingText, setName, setRecording, type AssetSegment } from './state/audioSegments'
import { useCurrentRecorder } from '~/components/ProductPreview/hooks/useCurrentRecorder'
import { nonBlank, react } from '~/commons'
import * as AIGreeting from '~/components/AIGreeting'
import { AudioPlayer } from '~/components/AudioPlayer'
import { NameInput } from '~/components/NameInput'
import { NameSelector, type NameItem } from '~/components/NameSelector'

type Segments = ReadonlyArray<domain.AudioSegment.Type>

export interface AudioSegmentsProps {
  value: Segments
  onChange: (next: Segments) => void
  productId: number
  config: domain.Api.Config.Type
  options: RD.RemoteData<domain.Error.Type, domain.Api.Options.Type>
}

export const AudioSegments: React.FC<AudioSegmentsProps> = ({ value, onChange, productId, config, options }) => {
  const hasPreRecorded = React.useMemo(() => pipe(value, RA.exists(domain.AudioSegment.ADT.is.preRecorded)), [value])
  const availableNames = useAvailableNames(productId, hasPreRecorded)
  const { currentRecorder, onIsRecording, onAccordionSelect } = useCurrentRecorder()

  const revokeObjectUrlAt = React.useCallback(
    (fieldId: string) =>
      pipe(
        value,
        RA.findFirst(s => s.fieldId === fieldId),
        O.chain(objectUrlOf),
        O.map(url => URL.revokeObjectURL(url))
      ),
    [value]
  )

  const onAudio = React.useCallback(
    (seg: AssetSegment) => (blob: Blob) => {
      revokeObjectUrlAt(seg.fieldId)
      onChange(setRecording(seg, blob, URL.createObjectURL(blob))(value))
    },
    [revokeObjectUrlAt, onChange, value]
  )

  const onDiscardAt = React.useCallback(
    (seg: AssetSegment) => () => {
      revokeObjectUrlAt(seg.fieldId)
      onChange(discard(seg)(value))
    },
    [revokeObjectUrlAt, onChange, value]
  )

  return (
    <>
      {pipe(
        value,
        RA.mapWithIndex((index, segment) =>
          pipe(
            segment,
            domain.AudioSegment.ADT.matchStrict<React.ReactNode>({
              // gated on availableNames success (faithful to old code)
              preRecorded: s =>
                pipe(
                  availableNames,
                  RD.toOption,
                  O.fold(
                    () => null,
                    (names: ReadonlyArray<NameItem>) => (
                      <NameSelector
                        key={s.fieldId}
                        title={s.title}
                        description={s.description}
                        names={names}
                        index={index}
                        onSelectName={item => onChange(setName(s, nonBlank(item.name))(value))}
                        onClearName={() => onChange(setName(s, O.none)(value))}
                      />
                    )
                  )
                ),
              // gated on options success (faithful to old code)
              templateName: s =>
                pipe(
                  options,
                  RD.toOption,
                  O.fold(
                    () => null,
                    ttsOptions => (
                      <NameInput.View
                        key={s.fieldId}
                        id={s.fieldId}
                        label={s.title}
                        description={s.description}
                        value={pipe(
                          s.name,
                          O.getOrElse(() => '')
                        )}
                        disabled={false}
                        config={config}
                        ttsOptions={ttsOptions}
                        onChange={v => onChange(setName(s, nonBlank(v))(value))}
                      />
                    )
                  )
                ),
              userUpload: s => (
                <UserUploadControl
                  key={s.fieldId}
                  segment={s}
                  options={options}
                  currentRecorder={currentRecorder}
                  onIsRecording={onIsRecording}
                  onAccordionSelect={onAccordionSelect}
                  onRecordingComplete={onAudio(s)}
                  onDiscard={onDiscardAt(s)}
                />
              ),
              // the old 'pers-sprecherwidmung-ai' block, rewired to onChange
              aiGreeting: s => (
                <AudioRecorderWrapper key={s.fieldId}>
                  {pipe(
                    s.audio,
                    O.fold(
                      () => (
                        <AIGreeting.View
                          config={config}
                          value={s.text}
                          onChange={v => onChange(setGreetingText(s, v)(value))}
                          onAudioGenerated={onAudio(s)}
                        />
                      ),
                      () => <TextContainer>Deine Wunschbotschaft</TextContainer>
                    )
                  )}
                  {pipe(
                    s.objectUrl,
                    O.foldMap(react.monoid)(url => <AudioPlayer src={url} onDiscard={onDiscardAt(s)} />)
                  )}
                </AudioRecorderWrapper>
              )
            })
          )
        )
      )}
    </>
  )
}
